import { useRef, useState } from "react";
import { Link } from "react-router-dom";

interface FinalCTAProps {
  heading?: string;
  subtitle?: string;
}

export default function FinalCTA({
  heading = "Your Roof Has One Job: Protect Your Home. Let's Make Sure It's Doing It.",
  subtitle = "Don't let a small leak turn into a $15,000 structural repair. Get an honest, free inspection from Central Florida's most trusted roofing team — today.",
}: FinalCTAProps) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);

    try {
      await fetch("https://readdy.ai/api/form/d6unfb51l1ubpb1296tg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-[#111111] py-20 lg:py-28 relative overflow-hidden">
      {/* Background roofing image overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-10"
        style={{
          backgroundImage: `url(https://public.readdy.ai/ai/img_res/e6ba7ee33fcf8f3bb255948b122608a8.jpg)`,
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Urgency banner */}
        <div className="bg-[#10B981] rounded-lg p-4 flex items-center justify-center gap-3 mb-12">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-alarm-warning-fill text-white text-base"></i>
          </div>
          <p className="text-white font-body font-700 text-sm text-center">
            Small roof damage doubles in repair cost every 3–6 months of delay. Act before it becomes a crisis.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          {/* Left side */}
          <div>
            <span className="text-[#10B981] font-body font-600 text-sm tracking-widest uppercase mb-3 block">
              Free Inspection
            </span>
            <h2 className="font-headline font-800 text-white text-3xl lg:text-4xl xl:text-5xl mb-5 leading-[1.1]">
              {heading}
            </h2>
            <p className="text-white/65 font-body text-base leading-relaxed mb-10">
              {subtitle}
            </p>

            <div className="flex flex-col gap-4 mb-10">
              {[
                { icon: "ri-search-eye-line", title: "Free Inspection", text: "No cost, no commitment — just honest answers" },
                { icon: "ri-time-line", title: "Same or Next Day", text: "Fast scheduling for urgent roofing situations" },
                { icon: "ri-shield-check-line", title: "Licensed & Insured", text: "Fully covered — your home is protected throughout" },
                { icon: "ri-price-tag-3-line", title: "Transparent Pricing", text: "Written estimate before any work begins" },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#10B981]/15 shrink-0">
                    <i className={`${item.icon} text-[#10B981] text-base`}></i>
                  </div>
                  <div>
                    <span className="font-body font-700 text-white text-sm block">{item.title}</span>
                    <span className="text-white/55 font-body text-sm">{item.text}</span>
                  </div>
                </div>
              ))}
            </div>

            <a
              href="tel:407-408-0577"
              className="inline-flex items-center gap-3 bg-white/8 hover:bg-white/15 text-white font-body font-700 text-sm px-6 py-4 rounded-md cursor-pointer whitespace-nowrap transition-colors border border-white/15"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#10B981]/20">
                <i className="ri-phone-fill text-[#10B981] text-sm"></i>
              </div>
              <div>
                <span className="text-white/50 text-[10px] block leading-none mb-0.5">Emergency Line</span>
                Call Now: (407) 408-0577
              </div>
            </a>
          </div>

          {/* Form */}
          <div className="bg-white rounded-lg p-8 lg:p-10">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#10B981]/15 mx-auto mb-5">
                  <i className="ri-checkbox-circle-fill text-[#10B981] text-4xl"></i>
                </div>
                <h3 className="font-headline font-700 text-[#1A1A1A] text-2xl mb-2">We'll Be in Touch!</h3>
                <p className="text-[#6B7280] font-body text-sm leading-relaxed">
                  Expect a call within one business hour. We'll schedule your free inspection at a time that works for you.
                </p>
              </div>
            ) : (
              <>
                <h3 className="font-headline font-800 text-[#1A1A1A] text-2xl mb-1">Schedule Your Free Roof Inspection</h3>
                <p className="text-[#6B7280] font-body text-sm mb-6">Takes 60 seconds. We'll do the rest.</p>
                <form
                  ref={formRef}
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="flex flex-col gap-4"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="font-body font-600 text-[#1A1A1A] text-xs mb-1.5 block">Full Name *</label>
                      <input
                        name="name"
                        required
                        type="text"
                        placeholder="John Smith"
                        className="w-full border border-gray-200 rounded-md px-3 py-2.5 font-body text-sm text-[#1A1A1A] placeholder-gray-400 focus:outline-none focus:border-[#10B981] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="font-body font-600 text-[#1A1A1A] text-xs mb-1.5 block">Phone *</label>
                      <input
                        name="phone"
                        required
                        type="tel"
                        placeholder="(719) 000-0000"
                        className="w-full border border-gray-200 rounded-md px-3 py-2.5 font-body text-sm text-[#1A1A1A] placeholder-gray-400 focus:outline-none focus:border-[#10B981] transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="font-body font-600 text-[#1A1A1A] text-xs mb-1.5 block">Email</label>
                    <input
                      name="email"
                      type="email"
                      placeholder="john@email.com"
                      className="w-full border border-gray-200 rounded-md px-3 py-2.5 font-body text-sm text-[#1A1A1A] placeholder-gray-400 focus:outline-none focus:border-[#10B981] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="font-body font-600 text-[#1A1A1A] text-xs mb-1.5 block">Service Needed</label>
                    <select
                      name="service"
                      className="w-full border border-gray-200 rounded-md px-3 py-2.5 font-body text-sm text-[#1A1A1A] focus:outline-none focus:border-[#10B981] transition-colors bg-white cursor-pointer"
                    >
                      <option value="">Select a service...</option>
                      <option value="Roof Repair">Roof Repair</option>
                      <option value="Roof Replacement">Roof Replacement</option>
                      <option value="Roof Inspection">Roof Inspection</option>
                      <option value="Storm Damage Repair">Storm Damage Repair</option>
                      <option value="Leak Detection">Leak Detection &amp; Fix</option>
                      <option value="Other">Other / Not Sure</option>
                    </select>
                  </div>
                  <div>
                    <label className="font-body font-600 text-[#1A1A1A] text-xs mb-1.5 block">Message</label>
                    <textarea
                      name="message"
                      maxLength={500}
                      rows={3}
                      placeholder="Describe your roofing concern..."
                      className="w-full border border-gray-200 rounded-md px-3 py-2.5 font-body text-sm text-[#1A1A1A] placeholder-gray-400 focus:outline-none focus:border-[#10B981] transition-colors resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-[#10B981] hover:bg-emerald-500 disabled:opacity-70 text-white font-body font-700 text-sm py-4 rounded-md cursor-pointer whitespace-nowrap transition-colors"
                  >
                    {loading ? "Sending..." : "Get My Free Inspection →"}
                  </button>
                  <div className="flex items-center justify-center gap-4 pt-1">
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-shield-check-fill text-[#10B981] text-xs"></i>
                      </div>
                      <span className="text-[#6B7280] font-body text-xs">Licensed & Insured</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-star-fill text-amber-400 text-xs"></i>
                      </div>
                      <span className="text-[#6B7280] font-body text-xs">5-Star Rated</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-time-line text-[#10B981] text-xs"></i>
                      </div>
                      <span className="text-[#6B7280] font-body text-xs">Same-Day Response</span>
                    </div>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
